import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { FaCalendarAlt, FaTimes, FaCheck } from 'react-icons/fa';
import AppDropdown from '../../../components/AppDropdown';
import AnalogTimePicker from './AnalogTimePicker';
import UserSearchInput from './UserSearchInput';
import LeadCustomerInput from './LeadCustomerInput';
import '../CRM.css';

const FOLLOWUP_TYPES = ['Call', 'WhatsApp', 'Email', 'Meeting', 'Site Visit', 'Quotation Follow-up', 'Payment Follow-up', 'Other'];
const TYPE_OPTIONS = FOLLOWUP_TYPES.map((t) => ({ value: t, label: t }));
const PRIORITY_OPTIONS = ['High', 'Medium', 'Low'].map((p) => ({ value: p, label: p }));

export const FollowupModal = ({ open, onClose, lead, onSave }) => {
  const buildForm = () => ({
    leadCustomer: lead ? `${lead.customerName || ''}${lead.company ? ' — ' + lead.company : ''}` : '',
    date: new Date().toISOString().slice(0, 10),
    time: '10:00',
    type: 'Call',
    priority: 'Medium',
    notes: '',
    assignedTo: '',
    status: 'Pending',
    leadId: lead?.id ?? null,
  });
  const [form, setForm] = useState(buildForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(buildForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lead]);

  if (!open) return null;
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSelectTarget = (item) => {
    setForm((p) => ({
      ...p,
      leadCustomer: item.label,
      leadId: item.kind === 'Lead' ? item.id : null,
      customerId: item.kind === 'Customer' ? item.id : null,
    }));
  };

  const handleLeadCustomerText = (t) =>
    setForm((p) => ({
      ...p,
      leadCustomer: t,
      leadId: null,
      customerId: null,
    }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
    } catch {
      toast.error('Failed to save follow-up.');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="crm-modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="crm-modal" role="dialog" aria-modal="true">
        <div className="crm-modal-header">
          <h3><FaCalendarAlt aria-hidden="true" /> Create Follow-up</h3>
          <button type="button" className="crm-modal-close" onClick={onClose} aria-label="Close dialog"><FaTimes aria-hidden="true" /></button>
        </div>
        <div className="crm-modal-body">
          <div className="crm-form-grid">
            <LeadCustomerInput
              label="Lead / Customer"
              value={form.leadCustomer}
              onChange={handleLeadCustomerText}
              onSelect={handleSelectTarget}
            />
            <div className="crm-field">
              <label>Date <span className="req">*</span></label>
              <input type="date" value={form.date} onChange={set('date')} />
            </div>
            <div className="crm-field">
              <AnalogTimePicker
                label="Time"
                value={form.time}
                onChange={(v) => setForm((p) => ({ ...p, time: v }))}
              />
            </div>
            <div className="crm-field">
              <label>Type <span className="req">*</span></label>
              <AppDropdown value={form.type} options={TYPE_OPTIONS}
                onChange={(v) => setForm((p) => ({ ...p, type: v }))}
                menuWidth={220} menuClassName="crm-dropdown-window" />
            </div>
            <div className="crm-field">
              <label>Priority</label>
              <AppDropdown value={form.priority} options={PRIORITY_OPTIONS}
                onChange={(v) => setForm((p) => ({ ...p, priority: v }))}
                menuWidth={180} menuClassName="crm-dropdown-window" />
            </div>
            <div className="crm-field">
              <UserSearchInput label="Assigned To" value={form.assignedTo}
                onChange={(val) => setForm((p) => ({ ...p, assignedTo: val }))} />
            </div>
            <div className="crm-field crm-field-full">
              <label>Notes</label>
              <textarea value={form.notes} onChange={set('notes')} placeholder="What is this follow-up about?" />
            </div>
          </div>
        </div>
        <div className="crm-modal-footer">
          <button type="button" className="crm-btn crm-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="crm-btn crm-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : <><FaCheck aria-hidden="true" /> Create Follow-up</>}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FollowupModal;